import React, { useCallback, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import moment from 'moment';
import { StandardProductForm, SeatedProductForm } from './StocksProductForm';
import { normalizeEventType } from '../../utils/eventFormUtils';
import { generateTicketID } from '../../utils/formatting';

const formatStockDateTimeLabel = (stock) => {
  const date = stock?.date ? moment(stock.date, 'YYYY-MM-DD', true) : null;
  const datePart = date?.isValid() ? date.format('DD/MM/YYYY') : '-';
  return stock?.time ? `${datePart} - ${stock.time}` : datePart;
};

const emptyProduct = () => ({
  id: generateTicketID(),
  status: 0,
  refundable: false,
  hasDownPayment: false,
  hasBasketLimit: false,
  sold: 0,
  price: 0,
  stock: 0,
});

/** Bu sayının üstündeki gruplarda ürünler kapalı gelir; her biri ayrı form + zengin metin editörü. */
const COLLAPSE_THRESHOLD = 10;

const productTitle = (item, sourceLang) => {
  const title = item?.title;
  if (!title) return '';
  if (typeof title === 'string') return title.trim();
  return String(title[sourceLang] || title.tr || title.en || '').trim();
};

/**
 * Eski SimpleFormIterator görünümü (+ / − ikonları),
 * ekleme setValue ile — nested `_stockRecord` altında ArrayInput Add bozuktu.
 */
const ProductGroupEditor = ({
  title,
  source,
  note,
  sourceLang,
  isEditForm = true,
  allowRemove = true,
  allowAdd = true,
  ProductFields = StandardProductForm,
  isInvitation = false,
}) => {
  const { getValues, setValue } = useFormContext();
  const watched = useWatch({ name: source });
  const items = Array.isArray(watched) ? watched : [];
  const collapsible = items.length > COLLAPSE_THRESHOLD;
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggleExpanded = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleAdd = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      const current = getValues(source);
      const list = Array.isArray(current) ? [...current] : [];
      const product = emptyProduct();
      list.push(product);
      setValue(source, list, { shouldDirty: true, shouldTouch: true });
      setExpandedIds((prev) => new Set(prev).add(product.id));
    },
    [getValues, setValue, source],
  );

  const handleRemove = useCallback(
    (index) => (event) => {
      event.preventDefault();
      event.stopPropagation();
      const current = getValues(source);
      const list = Array.isArray(current) ? [...current] : [];
      list.splice(index, 1);
      setValue(source, list, { shouldDirty: true, shouldTouch: true });
    },
    [getValues, setValue, source],
  );

  return (
    <div style={{ border: '1px dashed #000', padding: 5 }}>
      <h3>
        {title}{' '}
        <span style={{ color: 'orange', fontStyle: 'italic', fontSize: 12 }}>
          (* all fields required)
        </span>
      </h3>
      {note && (
        <span
          style={{
            backgroundColor: '#0ABAB5',
            color: 'white',
            borderRadius: 10,
            fontSize: 12.5,
            padding: 5,
            marginTop: -10,
            marginBottom: 8,
            display: 'inline-block',
          }}
        >
          {note}
        </span>
      )}

      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {items.map((item, index) => {
          const getSource = (field) => `${source}.${index}.${field}`;
          const rowId = item?.id || `${source}-${index}`;
          const expanded = !collapsible || expandedIds.has(rowId);
          return (
            <li
              key={rowId}
              style={{
                display: 'flex',
                listStyleType: 'none',
                borderBottom: '1px solid #e0e0e0',
                paddingBottom: 8,
                marginBottom: 8,
              }}
            >
              <section style={{ flex: 1, minWidth: 0 }}>
                {collapsible && (
                  <Box
                    onClick={() => toggleExpanded(rowId)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 0.5,
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#6b7280', minWidth: 28 }}>
                      {index + 1}.
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      noWrap
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      {productTitle(item, sourceLang) || item?.id || '—'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#4b5563', whiteSpace: 'nowrap' }}>
                      {item?.price ?? 0} TL · stok {item?.stock ?? 0}
                    </Typography>
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        color: '#6b7280',
                        transition: 'transform 0.15s',
                        transform: expanded ? 'rotate(180deg)' : 'none',
                      }}
                    />
                  </Box>
                )}
                {expanded && (
                  <ProductFields
                    getSource={getSource}
                    showSold={false}
                    scopedFormData={item || {}}
                    isEditForm={isEditForm}
                    sourceLang={sourceLang}
                    isInvitation={isInvitation}
                  />
                )}
              </section>
              {allowRemove && (
                <span style={{ marginTop: 4, marginLeft: 4 }}>
                  <Tooltip title="Sil">
                    <IconButton
                      type="button"
                      size="small"
                      color="primary"
                      onClick={handleRemove(index)}
                      aria-label="remove"
                    >
                      <RemoveCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {allowAdd && (
        <div>
          <Tooltip title="Ekle">
            <IconButton
              type="button"
              size="small"
              color="primary"
              onClick={handleAdd}
              aria-label="add"
              className={`button-add button-add-${source}`}
            >
              <AddCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export const StandardProductFormComponent = ({ stock, formPrefix = '', sourceLang = 'tr' }) => {
  const stockType = normalizeEventType(stock?.type);
  const locked = stockType === 3;

  if (locked) {
    return (
      <p style={{ color: '#6b7280' }}>
        Seated stock products are managed via layout seat assignments.
      </p>
    );
  }

  return (
    <React.Fragment>
      <h2>Details</h2>
      <div style={{ fontSize: 14 }}>
        <div>
          <span>
            <b>Stock ID:&nbsp;</b>
          </span>
          <span>{stock?.id ?? '-'}</span>
        </div>
        <div>
          <span>
            <b>Date / Time:&nbsp;</b>
          </span>
          <span>{formatStockDateTimeLabel(stock)}</span>
        </div>
      </div>

      <h2>Products / Stocks</h2>
      <Box display={{ xs: 'block', sm: 'flex', width: '100%', marginBottom: 20 }}>
        <Box flex={1} mr={{ xs: 0, sm: '0.5em' }}>
          <ProductGroupEditor
            title="Special Deal"
            source={`${formPrefix}stock.deal`}
            sourceLang={sourceLang}
            isEditForm
          />
        </Box>
        <Box flex={1} ml={{ xs: 0, sm: '0.5em' }}>
          <ProductGroupEditor
            title="Reservation"
            source={`${formPrefix}stock.guestlist`}
            sourceLang={sourceLang}
            isEditForm={false}
          />
        </Box>
      </Box>

      <Box display={{ xs: 'block', sm: 'flex', width: '100%', marginBottom: 20 }}>
        <Box flex={1} mr={{ xs: 0, sm: '0.5em' }}>
          <ProductGroupEditor
            title="General"
            source={`${formPrefix}stock.general`}
            sourceLang={sourceLang}
            isEditForm
          />
        </Box>
        <Box flex={1} ml={{ xs: 0, sm: '0.5em' }}>
          <ProductGroupEditor
            title="VIP List"
            source={`${formPrefix}stock.vip`}
            sourceLang={sourceLang}
            isEditForm
          />
        </Box>
      </Box>

      <Box display={{ xs: 'block', sm: 'flex', width: '100%' }}>
        <Box flex={1} mr={{ xs: 0, sm: '0.5em' }}>
          <ProductGroupEditor
            title="Extras"
            source={`${formPrefix}stock.extras`}
            sourceLang={sourceLang}
            isEditForm
          />
        </Box>
        <Box flex={1} ml={{ xs: 0, sm: '0.5em' }}>
          <ProductGroupEditor
            title="Invitations"
            source={`${formPrefix}stock.invitation`}
            note="Can create invitations for guests to attend the event."
            sourceLang={sourceLang}
            isEditForm
            isInvitation
          />
        </Box>
      </Box>
    </React.Fragment>
  );
};

export const SeatedProductFormComponent = ({ stock, formPrefix = '', sourceLang = 'tr' }) => {
  const stockType = normalizeEventType(stock?.type);
  if (stockType === 3) {
    return (
      <p style={{ color: '#6b7280' }}>
        Seated stock products are managed via layout seat assignments.
      </p>
    );
  }

  return (
    <ProductGroupEditor
      title="Tickets"
      source={`${formPrefix}stock`}
      sourceLang={sourceLang}
      isEditForm
      allowRemove={false}
      ProductFields={SeatedProductForm}
    />
  );
};
